import React, { useMemo } from "react";
import { Facet } from "@linkfluence/models/facet";
import { ComparisonPeriod } from "@radarly/models/cards/period";
import { hideableTerms } from "@radarly/models/cards/term";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentTerm } from "@radarly/models/search";
import HorizontalBarChart from "../../dataviz/horizontal-bar-chart";
import AverageRating from "./average-rating";
import { useHorizontalBarChart } from "./model";

export interface SharedWidgetsTermHorizontalBarChartInnerProps {
  loading: boolean;
  data: SearchFragmentTerm["data"];
  comparaisonData: SearchFragmentTerm["data"];
  fragment: SearchFragmentTerm;
  dataviz: DatavizHorizontalBarChart;
  className?: string;
  onClickGauge?: (uid: string | number, status?: "include" | "exclude") => void;
  facet: FullFacet;
  disableAnimation?: boolean;
  comparisonPeriod?: ComparisonPeriod;
  isFullscreen?: boolean;
  isEdition?: boolean;
  isPNG?: boolean;
}

export default function SharedWidgetsTermHorizontalBarChartInner({
  loading,
  data,
  comparaisonData,
  fragment,
  dataviz,
  className,
  onClickGauge,
  facet,
  disableAnimation,
  comparisonPeriod,
  isFullscreen,
  isEdition,
  isPNG,
}: SharedWidgetsTermHorizontalBarChartInnerProps) {
  const isHideable = hideableTerms.includes(facet.id as Facet);
  const { sort, total, gauges, range, metric, withAverageRating, order } =
    useHorizontalBarChart({
      loading,
      data,
      comparaisonData,
      fragment,
      dataviz,
      facet,
    });

  const validGauges = useMemo(() => {
    if (isHideable) {
      const hiddenTerms = isHideable ? dataviz?.hiddenTerms || [] : [];
      if (isEdition) {
        return gauges.map((gauge) => ({
          ...gauge,
          isHidden: hiddenTerms.includes(gauge.label),
        }));
      }
      return gauges.filter((gauge) => !hiddenTerms.includes(gauge.label));
    }
    return gauges;
  }, [dataviz?.hiddenTerms, gauges, isEdition, isHideable]);

  return (
    <HorizontalBarChart
      dataviz={dataviz}
      gauges={validGauges}
      className={className}
      showIndex={!withAverageRating}
      showTrackWhenSuperNarrow={false}
      onClickGauge={onClickGauge}
      sort={sort}
      range={range}
      metric={metric}
      order={order as "asc" | "desc"}
      aside={
        withAverageRating && (
          <AverageRating gauges={validGauges} total={total} />
        )
      }
      total={total}
      disableAnimation={disableAnimation}
      comparisonPeriod={comparisonPeriod}
      isFullscreen={isFullscreen}
      isHideable={isHideable && isEdition}
      isPNG={isPNG}
    />
  );
}
